import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, CircularProgress, Alert, Tooltip,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { httpClient } from '../services/http-client';
import { InstitutionMark } from '../components/InstitutionMark';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';
import { useAuthStore } from '../stores/auth-store';

interface Cash { currency: string; amount: string }

interface AccountRow {
  account_id: string;
  name: string;
  institution: string;
  type: 'Asset' | 'Debt' | 'Investment';
  owner: string;
  owner_id: string;
  is_joint: boolean;
  currency: string;
  balance: string;
  balance_base: string;
  invested: string;
  cash: Cash[];
  positions: number;
  cash_known: boolean;
  // Day the balance was last valued (Plaid market value for a linked
  // investment account). Absent when never valued.
  last_valued_at?: string;
}

const num = (s?: string) => (s ? parseFloat(s) : 0);

const GROUPS: Array<{ key: AccountRow['type']; label: string }> = [
  { key: 'Investment', label: 'Investments' },
  { key: 'Asset', label: 'Cash & property' },
  { key: 'Debt', label: 'Debts' },
];

/**
 * Every account, grouped by what it is, with a route into the right detail.
 *
 * CURRENCY RULE, applied throughout: an account's own figures are shown in the
 * currency they are actually held in — a US margin account reads US$503,191,
 * never a bare "$503,191" that a Canadian reader will take as CAD. The base
 * currency appears only where figures of DIFFERENT currencies are combined
 * into one total, because that total exists in no other denomination.
 *
 * WHERE CLICKING GOES depends on the account. An investment account leads to
 * holdings and ACB; a chequing account to its transactions; a house or car to
 * its valuation and the loan against it. Sending all three to one generic page
 * would mean the useful view is always two clicks away.
 */
export const AccountsBrowserPage: React.FC = () => {
  const navigate = useNavigate();
  const baseCurrency = useBaseCurrency();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [scope, setScope] = useState<'mine' | 'household'>('mine');
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    httpClient
      .get<{ accounts: AccountRow[] }>('/accounts/overview')
      .then((r) => {
        if (cancelled) return;
        const list = ((r as any)?.data?.accounts ?? (r as any)?.accounts ?? []) as AccountRow[];
        setRows(Array.isArray(list) ? list : []);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message ?? 'Could not load accounts'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(
    () => rows.filter((a) =>
      scope === 'household' || a.owner_id === currentUserId || a.is_joint),
    [rows, scope, currentUserId],
  );

  /**
   * Totals convert to the base currency, because they combine accounts held in
   * different ones. This is the single place a converted figure is correct —
   * every per-account number stays in its own currency.
   */
  const totals = useMemo(() => {
    const t = { Investment: 0, Asset: 0, Debt: 0 } as Record<string, number>;
    visible.forEach((a) => { t[a.type] = (t[a.type] ?? 0) + num(a.balance_base); });
    return t;
  }, [visible]);

  /** Where a click should land, by what the account actually is. */
  const openAccount = (a: AccountRow) => {
    // Name the account in the URL. Without it the holdings page restores
    // whichever account was last viewed, so clicking one account showed
    // another's positions.
    if (a.type === 'Investment') {
      navigate({ to: `/holdings?account=${a.account_id}` as any });
      return;
    }
    else navigate({ to: `/accounts/${a.account_id}` as any });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>Accounts</Typography>
        <ToggleButtonGroup size="small" exclusive value={scope}
          onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value="mine">Mine</ToggleButton>
          <ToggleButton value="household">All accounts</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Balances are shown in the currency each account is held in. Totals are
        converted to {baseCurrency}, because they combine currencies.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {GROUPS.map((g) => {
        // Zero-balance accounts are SHOWN, sorted last.
        //
        // Hiding them made two Qtrade accounts invisible immediately after
        // they were created: the activity export carried no opening balance,
        // so they sat at $0 with real trades behind them and simply did not
        // appear. An account someone deliberately added is never noise, and
        // "it isn't there" is a far worse failure than a quiet row at the
        // bottom of the list.
        const group = visible
          .filter((a) => a.type === g.key)
          .sort((a, b) => {
            const az = num(a.balance) === 0 ? 1 : 0;
            const bz = num(b.balance) === 0 ? 1 : 0;
            if (az !== bz) return az - bz;
            return Math.abs(num(b.balance)) - Math.abs(num(a.balance));
          });
        if (group.length === 0) return null;
        return (
          <Box key={g.key} sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="overline" color="text.secondary">{g.label}</Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" fontWeight={700}>
                {formatCurrency(totals[g.key] ?? 0, baseCurrency as any,
                  { base: baseCurrency as any })}
              </Typography>
            </Stack>

            <Box sx={{
              display: 'grid', gap: 1.5,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' },
            }}>
              {group.map((a) => (
                <Paper
                  key={a.account_id}
                  onClick={() => openAccount(a)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openAccount(a); }}
                  sx={{
                    p: 2, cursor: 'pointer',
                    transition: 'border-color var(--dur-base)',
                    border: '1px solid var(--border-soft)',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <InstitutionMark name={a.institution || a.name} size={28} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={650} noWrap>{a.name}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {a.institution}
                        </Typography>
                        {a.is_joint
                          ? <Chip label="Joint" size="small" color="secondary" sx={{ height: 17 }} />
                          : <Chip label={a.owner || '—'} size="small" variant="outlined" sx={{ height: 17 }} />}
                      </Stack>
                    </Box>
                    {/* The account's OWN currency, always. */}
                    <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                      {formatCurrency(a.balance, a.currency as any, { base: baseCurrency as any })}
                    </Typography>
                  </Stack>

                  {a.type === 'Investment' && (
                    <>
                      <Divider sx={{ my: 1.25 }} />
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {a.positions > 0 && (
                          <Chip size="small" variant="outlined" sx={{ height: 20 }}
                            label={`Invested ${formatCurrency(a.invested, a.currency as any, { base: baseCurrency as any })}`} />
                        )}
                        {a.cash_known ? (
                          a.cash.filter((c) => num(c.amount) !== 0).map((c) => (
                            <Chip
                              key={c.currency}
                              size="small"
                              sx={{ height: 20 }}
                              color="success"
                              variant="outlined"
                              // Cash is labelled with the currency it is held
                              // in, which is not always the account's: IBKR
                              // reports USD cash inside a CAD-labelled account.
                              label={`Cash ${formatCurrency(c.amount, c.currency as any, { base: baseCurrency as any })}`}
                            />
                          ))
                        ) : (
                          <Tooltip title="No holdings have been imported for this account, so we cannot say how much of the balance is uninvested. Import an activity or holdings export to split it.">
                            <Chip size="small" variant="outlined" sx={{ height: 20 }}
                              label="Cash unknown" />
                          </Tooltip>
                        )}
                      </Stack>
                    </>
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
